import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import Modal from './Modal';
import {
  getPhotoGuidance,
  getVideoGuidance,
  shouldShowVideoGuidance,
  isVideoLive,
  DEFAULT_CONTENT_PHOTO_TYPE
} from '../../utils/mediaUploadSpecs';

/**
 * <MediaQualityHint>
 *
 * The "i" affordance next to a file picker: explains how to export so uploads
 * survive the pipeline looking their best. One component, one source of
 * numbers (src/utils/mediaUploadSpecs.js), reused by every upload surface.
 *
 * WHY A MODAL AND NOT A TOOLTIP
 * These pickers live inside other modals (VisitConfirmationModal, the recap
 * composer), inside horizontally-scrolling photo strips, and inside drawers. A
 * position-anchored popover has to solve clipping and stacking separately in
 * each of those; Modal already owns a modal stack that renders a nested modal
 * above its parent and gives the topmost one the keyboard, so nesting is the
 * behaviour we want for free.
 *
 * WHY NOT <FeatureHint>
 * FeatureHint is onboarding: it shows once, records dismissal, and never comes
 * back. This is reference material someone wants on their third upload, when
 * they finally care about it — so it has to stay available on demand.
 *
 * @param {string} photoType - Which spec to describe (default: content photos)
 * @param {boolean} showLabel - Render the text label beside the icon
 * @param {string} className - Extra classes for the trigger button
 */
function MediaQualityHint({
  photoType = DEFAULT_CONTENT_PHOTO_TYPE,
  showLabel = false,
  className = ''
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const photo = getPhotoGuidance(photoType);
  const video = getVideoGuidance();
  const showVideo = shouldShowVideoGuidance();
  const videoLive = isVideoLive();

  const triggerLabel = t('mediaQualityHint.triggerLabel');

  // Rows are data so the photo and video sections share one renderer and the
  // ordering stays obvious. Colour space leads deliberately: it's the only
  // setting here whose consequence is visible damage rather than wasted bytes.
  const photoRows = [
    { key: 'colorSpace', values: { colorSpace: photo.colorSpace } },
    { key: 'size', values: { longEdge: photo.longEdge } },
    { key: 'quality', values: { quality: photo.quality, maxSize: photo.maxSizeLabel } },
    { key: 'metadata', values: {} }
  ];

  // "We drop to N px on a weak connection" is only a warning when N is below
  // this surface's own ceiling. It isn't for every type — the slow-connection
  // fallback is one shared config (1920px), so on a 1600px banner it would be
  // an upgrade, and printing it would tell the user the opposite of the truth.
  if (photo.slowConnectionLongEdge < photo.longEdge) {
    photoRows.push({
      key: 'connection',
      values: { slowLongEdge: photo.slowConnectionLongEdge }
    });
  }

  const videoRows = [
    { key: 'format', values: { container: video.container, codec: video.codec, audioCodec: video.audioCodec } },
    { key: 'resolution', values: { recommendedHeight: video.recommendedHeight, maxHeight: video.maxHeight } },
    { key: 'frameRate', values: { frameRate: video.frameRate } },
    { key: 'length', values: { maxMinutes: video.maxMinutes, maxSize: video.maxSizeLabel, monthlyMinutes: video.monthlyMinutes } },
    { key: 'master', values: {} }
  ];

  const renderRows = (section, rows) => (
    <ul className="space-y-3">
      {rows.map(({ key, values }) => (
        <li key={key}>
          <p className="text-sm font-medium text-content-primary">
            {t(`mediaQualityHint.${section}.${key}.label`, values)}
          </p>
          <p className="text-sm text-content-secondary leading-snug">
            {t(`mediaQualityHint.${section}.${key}.body`, values)}
          </p>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        // Borderless on purpose — IconButton is the standard for square
        // *action* buttons (share, delete), and its bordered 44px box reads as
        // a primary control sitting next to a section heading. The touch
        // target still meets 44px on mobile and collapses on desktop.
        className={`inline-flex items-center gap-1 rounded-lg text-content-muted hover:text-content-secondary transition-colors duration-fast min-h-[44px] sm:min-h-0 sm:py-0.5 px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 ${className}`}
        title={triggerLabel}
        aria-label={triggerLabel}
      >
        <Icon name="info" className="w-4 h-4" />
        {showLabel && (
          <span className="text-xs font-medium">{triggerLabel}</span>
        )}
      </button>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        size="sm"
        showCloseButton
        ariaLabel={t('mediaQualityHint.heading')}
      >
        <div className="p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-content-primary mb-1">
            {t('mediaQualityHint.heading')}
          </h2>
          <p className="text-sm text-content-secondary mb-5 leading-snug">
            {t('mediaQualityHint.intro')}
          </p>

          <h3 className="text-xs font-semibold text-content-secondary uppercase tracking-wide mb-3">
            {t('mediaQualityHint.photo.heading')}
          </h3>
          {renderRows('photo', photoRows)}

          <p className="text-xs text-content-muted mt-4 leading-snug">
            {t('mediaQualityHint.photo.formatsNote', { formats: photo.formats })}
          </p>

          {showVideo && (
            <div className="mt-6 pt-5 border-t border-subtle">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-xs font-semibold text-content-secondary uppercase tracking-wide">
                  {t('mediaQualityHint.video.heading')}
                </h3>
                {!videoLive && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary-lighter text-primary">
                    {t('mediaQualityHint.video.comingSoonBadge')}
                  </span>
                )}
              </div>
              {renderRows('video', videoRows)}
              {video.provisional && (
                <p className="text-xs text-content-muted mt-4 leading-snug">
                  {t('mediaQualityHint.video.provisionalNote')}
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

export default MediaQualityHint;
